/**
 * Cosa succede quando si segna incassata una fattura (26/10/2026).
 *
 * "Segna incassata" sulla fattura intera (POST /invoices/:id/mark-paid senza
 * paymentId, invoices.routes.ts) chiude TUTTE le rate ancora aperte, con la
 * data di oggi e il bonifico come modo di pagamento. Se cosi' la fattura
 * risulta pagata del tutto, parte la mail di avviso «Fattura incassata»
 * (fireMail.ceoAlert, ad Andrea) e la notifica agli amministratori. Su
 * FattureInCloud non si scrive niente.
 *
 * Prima sul computer partiva senza chiedere; sul telefono la conferma diceva
 * solo "risulterà incassata oggi". Qui sta il testo che dice tutto, uguale nei
 * due posti: quali rate, con che data, che cosa parte.
 */
import type { ReactNode } from 'react';
import { fmtEur } from '../../lib/utils';
import { giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno } from '../admin/fatture/fattureApi';

/** Una rata come la manda GET /invoices/:id (PAYMENT_LITE_SELECT). */
export interface RataFattura {
  id: string;
  seq: number;
  amount: string | number;
  status: string;
  dueDate: string | null;
  paidDate: string | null;
}

/** Le rate che contano: quelle stornate (tolte da una nota di credito) no. */
export function rateVive(payments: RataFattura[] | null | undefined): RataFattura[] {
  return (payments ?? []).filter((p) => p.status !== 'STORNATA');
}

/** "la rata 1 di 3", oppure "l'unico pagamento" quando la rata e' una sola. */
export function nomeRata(p: Pick<RataFattura, 'seq'>, di: number): string {
  return di > 1 ? `la rata ${p.seq} di ${di}` : "l'unico pagamento";
}

/** "scaduta il 5 marzo", "scade oggi", "scade il 10 novembre". */
export function scadenzaRata(due: string | null): string {
  if (!due) return 'senza scadenza';
  const g = giorniDaOggi(due);
  if (g === 0) return 'scade oggi';
  return g != null && g < 0 ? `scaduta ${ilGiorno(due)}` : `scade ${ilGiorno(due)}`;
}

const euro = (n: number | string) => `€ ${fmtEur(Number(n), true)}`;

/** Chi riceve la mail di avviso: MAIL_CEO_BCC dell'API, cioe' Andrea. */
export const AVVISO_INCASSO = 'Parte la mail di avviso «Fattura incassata» ad Andrea.';

/**
 * Il corpo della conferma di "Segna incassata" sulla fattura intera.
 * `computer`: aggiunge la strada per un altro giorno o un altro modo (sul
 * computer le rate hanno ognuna il suo "Segna incassata" con data e modo).
 */
export function TestoConfermaIncasso({ payments, totale, computer }: {
  payments: RataFattura[] | null | undefined;
  totale: number | string;
  computer?: boolean;
}): ReactNode {
  const vive = rateVive(payments);
  const aperte = vive.filter((p) => p.status === 'DA_INCASSARE');
  // "rata 1 di 3" conta tutte le rate, come le righe della scheda (anche le stornate).
  const di = (payments ?? []).length;

  let cosa: ReactNode;
  if (aperte.length === 0) {
    cosa = <>La fattura, {euro(totale)}, risulterà <strong>pagata oggi, con bonifico</strong>.</>;
  } else if (aperte.length === 1) {
    const p = aperte[0]!;
    cosa = (
      <>Si chiude <strong>{nomeRata(p, di)}</strong> ({euro(p.amount)}, {scadenzaRata(p.dueDate)}) con la
        {' '}<strong>data di oggi</strong>, come pagata <strong>con bonifico</strong>.</>
    );
  } else {
    cosa = (
      <>
        Si chiudono <strong>{aperte.length} rate</strong> con la <strong>data di oggi</strong>, come pagate <strong>con bonifico</strong>:
        <ul className="fd-conf-rate">
          {aperte.map((p) => (
            <li key={p.id}><span>rata {p.seq} di {di}</span><span className="mono">{euro(p.amount)}</span><span className="mute">{scadenzaRata(p.dueDate)}</span></li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="fd-conf">
      <p>{cosa}</p>
      <p>Si segna soltanto qui: non si muove nessun soldo e su FattureInCloud non cambia niente.</p>
      <p><strong>{AVVISO_INCASSO}</strong></p>
      {computer && (
        <p className="mute">Ha pagato un altro giorno, in contanti o solo una parte? Annulla e usa «Segna incassata» sulla rata: lì scegli la data, il modo e il bonifico.</p>
      )}
    </div>
  );
}
